import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiGet } from "../../Api/Client.js";
import { CheckboxDropdown } from "../../Shared/ScreenComponents.jsx";
import { fmtCurrency, stageColor } from "./crmFormat.js";

// ── The pipeline, in the order a deal actually moves ──────────────────────────
// Every stage the board CAN show. Which ones actually render as columns is
// up to the stage dropdown below — not fixed here.
const ALL_STAGES = [
  "New Lead",
  "Contact Attempted",
  "Engaged",
  "Discovery / Demo Scheduled",
  "Demo Done / Solution Fit",
  "Proposal Sent",
  "Nurture / Recycle",
  "Closed - Won",
  "Closed - Lost",
  "Closed - Cold",
].map((key) => ({ key, color: stageColor(key) }));
// Default view: the stages an open deal is actually moving through. Not the
// very start of the funnel (New Lead / Contact Attempted / Engaged — high
// volume, low per-card attention) and not the closed stages (reference, not
// work) — both still one click away in the dropdown.
const DEFAULT_STAGES = ["Discovery / Demo Scheduled", "Demo Done / Solution Fit", "Proposal Sent"];
const PRODUCTS = ["Banao", "InterviewGod", "Vidya", "Vikaas"];
const PAGE_SIZE = 20;

const SOURCE_TONE = {
  "Banao Website": "var(--blue-500)", "Client Website": "var(--green-600)", LinkedIn: "#0077b5",
  Instagram: "#e1306c", InterviewGod: "var(--indigo-500)", Vikaas: "var(--red-600)", website: "var(--c-500)",
};
const srcTone = (s) => SOURCE_TONE[s] || "var(--c-500)";

function money(v, company) {
  return Number(v || 0) ? fmtCurrency(v, company) : null;
}
function daysFromNow(iso) {
  if (!iso) return null;
  return Math.round((new Date(iso) - Date.now()) / 86400000);
}
function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}
function initials(s) {
  return String(s || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
}

// ── Lead card ─────────────────────────────────────────────────────────────────
function LeadCard({ lead, navigate, company }) {
  const name = lead.full_name || lead.company_name || "Untitled Lead";
  const sub = lead.company_name && lead.full_name ? lead.company_name : (lead.industry || "");
  const overdueDays = daysFromNow(lead.next_follow_up_at);
  const overdue = overdueDays !== null && overdueDays < 0;
  const val = money(lead.estimated_value, company);
  return (
    <button className="Crm-Card" onClick={() => navigate(`/lms/${lead.id}/`)}>
      <div className="Crm-Card-Top">
        <span className="Crm-Card-Name">{name}</span>
        {lead.is_important && <span className="Crm-Star" title="Important">★</span>}
      </div>
      {sub && <div className="Crm-Card-Sub">{sub}</div>}
      {lead.latest_comment && lead.latest_comment !== "-" && (
        <div className="Crm-Card-Note">{lead.latest_comment}</div>
      )}
      <div className="Crm-Card-Meta">
        {lead.source && <span className="Crm-Src" style={{ background: `${srcTone(lead.source)}18`, color: srcTone(lead.source) }}>{lead.source}</span>}
        {val && <span className="Crm-Val">{val}</span>}
      </div>
      <div className="Crm-Card-Foot">
        <span className="Crm-Owner" title={lead.owner_name || "Unassigned"}>
          <span className="Crm-Avatar">{initials(lead.owner_name)}</span>
          {lead.owner_name || "Unassigned"}
        </span>
        {lead.next_follow_up_at ? (
          <span className={"Crm-Fu" + (overdue ? " over" : "")}>
            {overdue ? `${-overdueDays}d overdue` : fmtDate(lead.next_follow_up_at)}
          </span>
        ) : (
          <span className="Crm-Fu none">no next-touch</span>
        )}
      </div>
    </button>
  );
}

// ── A single column — owns its own paging, so scrolling it loads more cards
//    in place instead of bouncing to a "+N more" link away from the board. ──
function BoardColumn({ stageDef, navigate, fetchParams, company }) {
  const [cards, setCards] = useState([]);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const bodyRef = useRef(null);
  const requestId = useRef(0);

  const load = useCallback(async (nextOffset, replace) => {
    const myRequest = ++requestId.current;
    setLoading(true);
    setFailed(false);
    try {
      const params = new URLSearchParams({
        stage: stageDef.key, limit: String(PAGE_SIZE), offset: String(nextOffset),
        sort_by: "-next_follow_up_at", skip_counts: "1",
      });
      if (fetchParams.owner) params.set("owner", fetchParams.owner);
      if (fetchParams.product) params.set("product", fetchParams.product);
      const resp = await apiGet(`/Lms/api/leads/?${params.toString()}`);
      if (myRequest !== requestId.current) return; // a newer filter change superseded this
      const results = resp?.results || [];
      setCards((prev) => (replace ? results : [...prev, ...results]));
      setOffset(nextOffset + results.length);
      setHasMore(results.length === PAGE_SIZE);
      if (typeof resp?.count === "number") setTotal(resp.count);
    } catch {
      // A failed load is not an empty column: say so, and let the person try again.
      if (myRequest === requestId.current) setFailed(true);
    } finally {
      if (myRequest === requestId.current) setLoading(false);
    }
  }, [stageDef.key, fetchParams.owner, fetchParams.product]);

  // Owner/product changed (or the column just became visible): start this
  // column over from the first page.
  useEffect(() => { load(0, true); }, [load]);

  const onScroll = () => {
    const el = bodyRef.current;
    if (!el || loading || failed || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) load(offset, false);
  };

  return (
    <section className="Crm-Col">
      <div className="Crm-Col-Head" style={{ borderTopColor: stageDef.color }}>
        <span className="Crm-Col-Dot" style={{ background: stageDef.color }} />
        <span className="Crm-Col-Name">{stageDef.key}</span>
        <span className="Crm-Col-Count">{total ?? cards.length}</span>
      </div>
      <div className="Crm-Col-Body" ref={bodyRef} onScroll={onScroll}>
        {cards.length === 0 && !loading && !failed && <div className="Crm-Empty">Nothing here</div>}
        {failed && (
          <div className="Crm-Empty">
            Could not load these leads. <button type="button" className="Crm-Retry" onClick={() => load(offset, offset === 0)}>Try again</button>
          </div>
        )}
        {cards.map((lead) => <LeadCard key={lead.id} lead={lead} navigate={navigate} company={company} />)}
        {loading && <div className="Crm-Col-Loading">Loading…</div>}
      </div>
    </section>
  );
}

// ── Board ─────────────────────────────────────────────────────────────────────
export function SalesCrmBoardScreen({ navigate, data }) {
  // A non-superadmin sees only their own pipeline and only their own name in the
  // owner picker — no toggling to anyone else. Super admins see everyone.
  const isSuper = !!data?.me?.user?.isSuperuser;
  const myId = String(data?.me?.employees?.[0]?.id || (data?.employees || []).find((e) => String(e.user) === String(data?.me?.user?.id))?.id || "");
  const [meta, setMeta] = useState({ stage_counts: {}, total_open: 0, origin_counts: {}, count: 0 });
  const [bas, setBas] = useState([]);
  const [owner, setOwner] = useState(isSuper ? "all" : (myId || "all"));
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaError, setMetaError] = useState(false);

  // Which stages show as columns, and which products are included — both
  // remembered per person, the same way the leads table remembers its
  // column choices, so this doesn't need re-picking every visit.
  const meKey = String(data?.me?.id ?? data?.me?.username ?? "anon");
  const STAGES_KEY = `crm.board.stages.v1.${meKey}`;
  const PRODUCTS_KEY = `crm.board.products.v1.${meKey}`;
  const [visibleStages, setVisibleStages] = useState(() => {
    try {
      const raw = localStorage.getItem(STAGES_KEY);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr) && arr.length) return new Set(arr);
    } catch { /* private mode */ }
    return new Set(DEFAULT_STAGES);
  });
  const [productFilter, setProductFilter] = useState(() => {
    try {
      const raw = localStorage.getItem(PRODUCTS_KEY);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr)) return new Set(arr); // may legitimately be all 4 (= "no filter")
    } catch { /* private mode */ }
    return null; // null = every product, same as not filtering at all
  });
  useEffect(() => {
    try { localStorage.setItem(STAGES_KEY, JSON.stringify([...visibleStages])); } catch { /* ignore */ }
  }, [visibleStages, STAGES_KEY]);
  useEffect(() => {
    try { localStorage.setItem(PRODUCTS_KEY, JSON.stringify(productFilter ? [...productFilter] : PRODUCTS)); } catch { /* ignore */ }
  }, [productFilter, PRODUCTS_KEY]);

  // The KPI strip + source strip + owner list — one light query, independent
  // of which columns/stages are currently chosen to display.
  useEffect(() => {
    let alive = true;
    (async () => {
      setMetaLoading(true);
      setMetaError(false);
      const ownerParam = owner !== "all" ? `&owner=${owner}` : "";
      try {
        const metaResp = await apiGet(`/Lms/api/leads/?limit=0${ownerParam}`);
        if (!alive) return;
        setMeta(metaResp || {});
        if (metaResp?.bas?.length) setBas(metaResp.bas);
      } catch {
        if (alive) setMetaError(true);
      } finally {
        if (alive) setMetaLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [owner]);

  const won = meta.stage_counts?.["Closed - Won"] || 0;
  const topSources = useMemo(
    () => Object.entries(meta.origin_counts || {}).sort((a, b) => b[1] - a[1]).slice(0, 5),
    [meta.origin_counts]
  );

  const columns = ALL_STAGES.filter((s) => visibleStages.has(s.key));
  const fetchParams = useMemo(() => ({
    owner: owner !== "all" ? owner : "",
    product: productFilter && productFilter.size < PRODUCTS.length ? [...productFilter].join(",") : "",
  }), [owner, productFilter]);

  return (
    <div className="Crm-Screen">
      <style>{CRM_CSS}</style>

      {/* Header + KPIs */}
      <header className="Crm-Head">
        <div>
          <h1 className="Crm-Title">Sales CRM <span className="Crm-Badge">Pipeline</span></h1>
          <p className="Crm-Sub">Your live book — work it left to right. Cold &amp; lost are out of the way.</p>
        </div>
        <div className="Crm-Kpis">
          {isSuper ? (
            <select className="Crm-Owner-Select" value={owner} onChange={(e) => setOwner(e.target.value)} title="Filter pipeline by owner">
              <option value="all">Everyone</option>
              {bas.map((b) => <option key={b.id} value={b.id}>{b.display_name}</option>)}
            </select>
          ) : (
            <select className="Crm-Owner-Select" value={owner} onChange={() => {}} disabled title="You see only your own pipeline">
              <option value={myId || "all"}>{bas.find((b) => String(b.id) === myId)?.display_name || "My pipeline"}</option>
            </select>
          )}
          <CheckboxDropdown
            className="Crm-Filter-Dropdown"
            label="Product"
            options={PRODUCTS.map((p) => ({ value: p, label: p }))}
            selected={productFilter || new Set(PRODUCTS)}
            onToggle={(value) => setProductFilter((prev) => {
              const base = new Set(prev || PRODUCTS);
              if (base.has(value)) base.delete(value); else base.add(value);
              return base;
            })}
            onSelectAll={() => setProductFilter(null)}
            onClear={() => setProductFilter(new Set())}
          />
          <CheckboxDropdown
            className="Crm-Filter-Dropdown"
            label="Stage"
            options={ALL_STAGES.map((s) => ({ value: s.key, label: s.key, color: s.color }))}
            selected={visibleStages}
            onToggle={(value) => setVisibleStages((prev) => {
              const base = new Set(prev);
              if (base.has(value)) base.delete(value); else base.add(value);
              return base;
            })}
            onSelectAll={() => setVisibleStages(new Set(ALL_STAGES.map((s) => s.key)))}
            onClear={() => setVisibleStages(new Set())}
          />
          <div className="Crm-Kpi"><b>{meta.total_open ?? 0}</b><span>Open deals</span></div>
          <div className="Crm-Kpi green"><b>{won}</b><span>Won (all-time)</span></div>
          <div className="Crm-Kpi muted"><b>{(meta.count ?? 0).toLocaleString("en-IN")}</b><span>Total leads</span></div>
        </div>
      </header>

      {metaError && <div className="Crm-Loading">The pipeline totals could not be loaded. Reload the page to try again.</div>}

      {/* Source strip */}
      {topSources.length > 0 && (
        <div className="Crm-Sources">
          <span className="Crm-Sources-Label">Where they come from</span>
          {topSources.map(([s, n]) => (
            <span key={s} className="Crm-Source-Pill">
              <span className="dot" style={{ background: srcTone(s) }} /> {s} <b>{n}</b>
            </span>
          ))}
        </div>
      )}

      {/* Board */}
      {metaLoading ? (
        <div className="Crm-Loading"><div className="Crm-Spinner" /> Loading your pipeline…</div>
      ) : columns.length === 0 ? (
        <div className="Crm-Loading">No stages selected — open the Stage dropdown above to choose what to show.</div>
      ) : (
        <div className="Crm-Board">
          {columns.map((s) => <BoardColumn key={`${s.key}|${fetchParams.owner}|${fetchParams.product}`} stageDef={s} navigate={navigate} fetchParams={fetchParams} company={data?.me?.company} />)}
        </div>
      )}
    </div>
  );
}

const CRM_CSS = `
.Crm-Screen { display:flex; flex-direction:column; height:100%; background:var(--surface-soft); overflow:hidden; }
.Crm-Head { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; padding:20px 24px 12px; flex-wrap:wrap; }
.Crm-Title { margin:0; font-size:22px; font-weight:700; color:var(--c-900); display:flex; align-items:center; gap:10px; }
.Crm-Badge { font-size:11px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; color:var(--indigo-500); background:var(--blue-100); padding:3px 8px; border-radius:20px; }
.Crm-Sub { margin:4px 0 0; font-size:13px; color:var(--c-500); }
.Crm-Kpis { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.Crm-Kpi { flex:1 1 auto; background:var(--surface); border:1px solid var(--c-200); border-radius:12px; padding:8px 16px; text-align:center; min-width:82px; }
.Crm-Kpi b { display:block; font-size:20px; color:var(--c-900); line-height:1.1; }
.Crm-Kpi span { font-size:11px; color:var(--c-400); }
.Crm-Kpi.green b { color:var(--green-600); }
.Crm-Kpi.muted b { color:var(--c-500); }
.Crm-Sources { display:flex; align-items:center; gap:8px; padding:4px 24px 12px; flex-wrap:wrap; }
.Crm-Sources-Label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:var(--c-400); margin-right:4px; }
.Crm-Source-Pill { display:inline-flex; align-items:center; gap:6px; font-size:12px; color:var(--c-600); background:var(--surface); border:1px solid var(--c-200); border-radius:20px; padding:4px 10px; }
.Crm-Source-Pill .dot { width:8px; height:8px; border-radius:50%; }
.Crm-Source-Pill b { color:var(--c-900); }
.Crm-Owner-Select { font-family:inherit; font-size:13px; font-weight:600; color:var(--c-900); background:var(--surface); border:1px solid var(--c-200); border-radius:12px; padding:12px 14px; min-width:150px; cursor:pointer; box-shadow:0 1px 2px rgba(15,23,42,.04); }
.Crm-Owner-Select:hover { border-color:var(--c-400); }
.Crm-Owner-Select:focus { outline:none; border-color:var(--indigo-500); box-shadow:0 0 0 2px rgba(79,70,229,.15); }
.Crm-Filter-Dropdown .Checkbox-Dropdown-Trigger { padding:11px 12px; }
.Crm-Board { flex:1; display:flex; gap:14px; overflow-x:auto; overflow-y:hidden; padding:4px 24px 20px; align-items:flex-start; }
.Crm-Col { flex:0 0 268px; width:268px; background:var(--c-200); border-radius:14px; display:flex; flex-direction:column; max-height:100%; }
.Crm-Col-Head { display:flex; align-items:center; gap:8px; padding:12px 12px 10px; border-top:3px solid; border-radius:14px 14px 0 0; background:var(--surface); }
.Crm-Col-Dot { width:9px; height:9px; border-radius:50%; flex-shrink:0; }
.Crm-Col-Name { font-size:13px; font-weight:600; color:var(--c-900); flex:1; line-height:1.2; }
.Crm-Col-Count { font-size:12px; font-weight:700; color:var(--c-500); background:var(--c-100); border-radius:20px; padding:1px 9px; }
.Crm-Col-Body { padding:10px; display:flex; flex-direction:column; gap:9px; overflow-y:auto; }
.Crm-Col-Loading { font-size:11.5px; color:var(--c-400); text-align:center; padding:8px 0; }
.Crm-Empty { font-size:12px; color:var(--c-300); text-align:center; padding:14px 0; }
.Crm-Retry { background:none; border:none; padding:0; color:var(--blue-600); font-size:12px; font-weight:600; cursor:pointer; text-decoration:underline; }
.Crm-Card { text-align:left; width:100%; background:var(--surface); border:1px solid var(--c-200); border-radius:11px; padding:11px 12px; cursor:pointer; font-family:inherit; transition:box-shadow .12s, transform .12s, border-color .12s; display:flex; flex-direction:column; gap:6px; }
.Crm-Card:hover { box-shadow:0 4px 14px rgba(15,23,42,.10); border-color:var(--blue-100); transform:translateY(-1px); }
.Crm-Card-Top { display:flex; align-items:flex-start; gap:6px; }
.Crm-Card-Name { font-size:13.5px; font-weight:600; color:var(--c-900); line-height:1.25; flex:1; overflow:hidden; text-overflow:ellipsis; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; }
.Crm-Star { color:var(--amber-500); font-size:13px; }
.Crm-Card-Sub { font-size:11.5px; color:var(--c-400); margin-top:-2px; }
.Crm-Card-Note { font-size:11.5px; color:var(--c-500); background:var(--surface-soft); border-radius:7px; padding:5px 7px; line-height:1.35; overflow:hidden; text-overflow:ellipsis; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; }
.Crm-Card-Meta { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.Crm-Src { font-size:10.5px; font-weight:600; border-radius:6px; padding:2px 7px; }
.Crm-Val { font-size:11px; font-weight:700; color:var(--blue-500); background:var(--blue-50); border-radius:6px; padding:2px 7px; }
.Crm-Card-Foot { display:flex; align-items:center; justify-content:space-between; gap:6px; margin-top:1px; }
.Crm-Owner { display:inline-flex; align-items:center; gap:5px; font-size:11px; color:var(--c-500); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.Crm-Avatar { width:18px; height:18px; border-radius:50%; background:var(--blue-100); color:var(--indigo-500); font-size:9px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.Crm-Fu { font-size:10.5px; font-weight:600; color:var(--c-500); background:var(--c-100); border-radius:6px; padding:2px 7px; white-space:nowrap; }
.Crm-Fu.over { color:var(--red-600); background:var(--red-100); }
.Crm-Fu.none { color:var(--c-300); background:transparent; }
.Crm-Loading { flex:1; display:flex; align-items:center; justify-content:center; gap:12px; color:var(--c-500); font-size:14px; }
.Crm-Spinner { width:22px; height:22px; border:3px solid var(--c-200); border-top-color:var(--indigo-500); border-radius:50%; animation:crmspin .8s linear infinite; }
@keyframes crmspin { to { transform:rotate(360deg); } }
@media (max-width:768px) { .Crm-Head { padding:16px; } .Crm-Board { padding:4px 12px 16px; } .Crm-Sources { padding:4px 16px 12px; } }
`;
